import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';

import {ProfileMain} from 'src/components/profile-tab-structure';
import {SaveWithReasonButton} from 'src/components/save-with-reason/save-with-reason-button';
import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {PersonalizationTabs} from '../personalization-tabs';

import {ProjectCodes, useProjectCodes} from './project-codes';

import type {SaveOptions} from 'src/components/save-with-reason/save-with-reason-button';


type Props = {
    userId: number,
    accountId: number,
};

const TAB_NAME = 'Activity Tags';

export const ActivityTags = ({userId, accountId}: Props) => {
    const {canEditUser} = useAdminAccessRight();
    const {userSubmit, userFieldChange} = useEventTracking();
    const {setSuccessMessage, setErrorMessage} = useContext(MessageContext);
    const [busy, setBusy] = useState<boolean>(false);

    const {isCurrentChunkChanged, setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'user-activity-tags',
        onProceed: () => {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const onFieldChange = (section: string) => (parameterName: string) => {
        userFieldChange({
            section,
            viewType: 'Existed',
            parameterName,
        });
    };

    const {
        isLoading: isProjectCodesLoading,
        loadingError: projectCodesLoadingError,
        isChanged: isProjectCodesChanged,
        data: projectCodes,
        setData: setProjectCodes,
        saveChanges: saveProjectCodes,
        discardChanges: discardProjectCodes,
    } = useProjectCodes({userId});

    useEffect(() => {
        setIsChunkChanged(isProjectCodesChanged);
    }, [isProjectCodesChanged, setIsChunkChanged]);

    const saveChanges = useCallback(async (options?: SaveOptions) => {
        const calls = [];

        setBusy(true);
        try {
            if (isProjectCodesChanged) {
                calls.push(await saveProjectCodes(options));
            }
            setBusy(false);

            const errors = [...new Set(calls.filter(Boolean))];
            if (errors.length) {
                setErrorMessage(errors.join('\n'));
                return errors.join('\n');
            } else {
                proceed();
            }
            userSubmit({submit: true, place: TAB_NAME});
        } finally {
            setBusy(false);
        }
    }, [
        isProjectCodesChanged,
        saveProjectCodes,
        proceed,
        setErrorMessage,
        userSubmit,
    ]);

    const discardChanges = useCallback(() => {
        discardProjectCodes();

        userSubmit({submit: false, place: TAB_NAME});
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [discardProjectCodes, setSuccessMessage, userSubmit]);

    const editDisabled = useMemo(() => !canEditUser(accountId), [canEditUser, accountId]);

    return (
        <>
            <PersonalizationTabs userId={userId} activeTab="activity-tags"/>
            <ProfileMain header={(
                <Item grow={1}>
                    <Group justify="space-between">
                        <Typography
                            fontWeight="semi-bold"
                            variant="heading-level-3">
                            {TAB_NAME}
                        </Typography>
                        {!editDisabled && (
                            <Group>
                                <Button
                                    variant="Type.SECONDARY"
                                    onClick={discardChanges}
                                    disabled={busy || !isCurrentChunkChanged}>
                                    Discard Changes
                                </Button>
                                <SaveWithReasonButton
                                    busy={busy}
                                    disabled={!isCurrentChunkChanged}
                                    onSave={saveChanges}
                                />
                            </Group>
                        )}
                    </Group>
                </Item>
            )}>
                <ProjectCodes
                    isLoading={isProjectCodesLoading}
                    loadingError={projectCodesLoadingError}
                    projectCodes={projectCodes}
                    setProjectCodes={setProjectCodes}
                    disabled={editDisabled}
                    onFieldChange={onFieldChange('Project Codes')}
                />
            </ProfileMain>
        </>
    );
};
